"use client";

import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@repo/ui";
import { formatDateTime, formatDuration } from "@repo/utils";
import { User, Clock, Calendar } from "lucide-react";
import { PamiChart } from "./PamiChart";
import {
  HexacoFactorCard,
  MotivationDimCard,
  QuestionsFinalesInfo,
} from "./PamiComponents";
import { HEXACO_FACTORS, MOTIVATION_DIMS } from "@repo/pami";
import { computeScores, extractScoresFromResult } from "./pami.utils";
import type {
  CandidatInfo,
  LLMSynthesis,
  PamiDonnees,
  PamiResultsProps,
  TraitResult,
} from "./pami.types";

const TABS = [
  { key: "synthese", label: "Synthèse" },
  { key: "personnalite", label: "Personnalité" },
  { key: "motivation", label: "Motivation" },
];

export function PamiResultsEcharts({
  donnees,
  candidatInfo,
  resultatsRacine,
}: PamiResultsProps) {
  const [activeTab, setActiveTab] = useState("synthese");
  const pamiDonnees = donnees as PamiDonnees;

  const { llmData, resultScores } = useMemo(() => {
    const latest = resultatsRacine?.at(-1);
    if (!latest?.data) return { llmData: null, resultScores: null };
    try {
      const parsed =
        typeof latest.data === "string" ? JSON.parse(latest.data) : latest.data;
      const p = parsed as Record<string, unknown>;
      if (Array.isArray(p.result)) {
        return {
          llmData: (p.llm_synthesis as LLMSynthesis) ?? null,
          resultScores: extractScoresFromResult(p.result as TraitResult[]),
        };
      }
      return {
        llmData:
          (parsed as { llm_synthesis?: LLMSynthesis })?.llm_synthesis ?? null,
        resultScores: null,
      };
    } catch {
      return { llmData: null, resultScores: null };
    }
  }, [resultatsRacine]);

  const scores = useMemo(
    () => resultScores ?? computeScores(pamiDonnees),
    [resultScores, pamiDonnees],
  );

  return (
    <div className="space-y-6">
      {/* ── Infos candidat ── */}
      <CandidatCard candidatInfo={candidatInfo} pamiDonnees={pamiDonnees} />

      {/* ── Présentation PAMI ── */}
      <PamiPresentationCard />

      {/* ── Onglets ── */}
      <div className="border-b -mx-4 md:-mx-8">
        <div className="flex flex-wrap px-4 md:px-8">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`shrink-0 px-5 py-3 text-sm transition-all cursor-pointer ${
                activeTab === tab.key
                  ? "font-semibold text-primary border-b-2 border-primary"
                  : "font-medium text-muted-foreground hover:text-foreground border-b-2 border-transparent"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Onglet Synthèse ── */}
      <div className={activeTab === "synthese" ? "space-y-4" : "hidden"}>
        <div>
          <h2 className="text-xl font-bold">Synthèse du profil</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Vue d'ensemble des 20 traits évalués par le PAMI.
          </p>
        </div>
        <Card>
          <CardContent className="p-0">
            <PamiChart scores={scores} />
          </CardContent>
        </Card>
      </div>

      {/* ── Onglet Personnalité ── */}
      <div className={activeTab === "personnalite" ? "space-y-4" : "hidden"}>
        <div>
          <h2 className="text-xl font-bold">Personnalité</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Analyse détaillée des 12 facettes de personnalité selon le modèle
            HEXACO.
          </p>
        </div>
        <div className="space-y-4">
          {HEXACO_FACTORS.map((factor) => (
            <HexacoFactorCard
              key={factor.key}
              factor={factor}
              scores={scores}
            />
          ))}
        </div>
      </div>

      {/* ── Onglet Motivation ── */}
      <div className={activeTab === "motivation" ? "space-y-4" : "hidden"}>
        <div>
          <h2 className="text-xl font-bold">Motivation</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Analyse des 8 dimensions motivationnelles selon le Work Values
            Inventory de Donald Super.
          </p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {MOTIVATION_DIMS.map((dim) => (
            <MotivationDimCard
              key={dim.prefix}
              dim={dim}
              score={scores[dim.prefix] ?? 5}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Sous-composants locaux à index ──────────────────────────────────────────

function CandidatCard({
  candidatInfo,
  pamiDonnees,
}: {
  candidatInfo: CandidatInfo;
  pamiDonnees: PamiDonnees;
}) {
  return (
    <Card>
      <CardContent className="py-4">
        <div className="flex flex-wrap gap-6 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <User className="h-4 w-4" />
            <span>
              {candidatInfo.prenom} {candidatInfo.nom} — {candidatInfo.email}
            </span>
          </div>
          {candidatInfo.dateFin && (
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              <span>{formatDateTime(candidatInfo.dateFin)}</span>
            </div>
          )}
          {candidatInfo.tempsPasse != null && (
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              <span>{formatDuration(candidatInfo.tempsPasse)}</span>
            </div>
          )}
          {pamiDonnees.questionsFinales && (
            <QuestionsFinalesInfo qf={pamiDonnees.questionsFinales} />
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function PamiPresentationCard() {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-sm font-semibold">
          Présentation du PAMI
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 text-sm text-muted-foreground space-y-3">
        <p>
          Développé par{" "}
          <strong className="text-foreground">Corail RH</strong>, le{" "}
          <strong className="text-foreground">PAMI</strong> (Personality and
          Motivation Inventory) est un test psychométrique conçu pour explorer
          la personnalité et les motivations en contexte professionnel au
          travers de <strong className="text-foreground">40 questions</strong>,
          avec un temps de passation moyen de{" "}
          <strong className="text-foreground">10 minutes</strong>.
        </p>
        <div>
          <p className="mb-2">
            Le PAMI évalue{" "}
            <strong className="text-foreground">20 traits au total</strong> :
          </p>
          <ul className="space-y-1.5 ml-1">
            <li className="flex items-start gap-2">
              <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-muted-foreground/50 shrink-0" />
              <span>
                <strong className="text-foreground">
                  12 traits de personnalité
                </strong>{" "}
                se référant au modèle{" "}
                <strong className="text-foreground">HEXACO</strong> (les Big 5 +
                le trait Honnêteté-Humilité)
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-muted-foreground/50 shrink-0" />
              <span>
                <strong className="text-foreground">
                  8 facteurs de motivation professionnelle
                </strong>{" "}
                se référant au{" "}
                <strong className="text-foreground">
                  Work Values Inventory
                </strong>{" "}
                de Donald Super
              </span>
            </li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}
